import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCAlert, RCCGrid, RCCHookToast, RCCRefDropzone, RCCRefModalProps, RCCServicesApi, RCCSwitch } from "react-common-components";
import { useForm, useWatch } from "react-hook-form";
import Attachments from "src/components/Attachments";
import CheckPermission from "src/components/CheckPermission";
import ListImportFiles from "src/components/ListImportFiles";
import LovDelimiter from "src/components/Lov/Delimiter";
import Modal from "src/components/Modal";
import Tabs from "src/components/Tabs";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { css } from "styled-components";
import CompaniesUser from "../CompaniesUser";
import GroupsUser from "../GroupsUser";
import { IComponentImportUsers, IRefImportUsers } from "./interface";
import StyledFieldset from "./styles";


const ModalImportUsers: React.ForwardRefRenderFunction<IRefImportUsers, IComponentImportUsers> = ({
	paginationRef
}, ref) => {

	const { translate } = useTranslation();
	const { businessGroup } = useAuth();
	const { addToast } = RCCHookToast();

	const { handleSubmit, control, register, clearErrors, formState: {errors}, setValue, reset } = useForm();
	
	const businessGroupId = useRef(businessGroup.efa001Id);
	const modalRef = useRef<RCCRefModalProps>(null);
	const dropZoneRef = useRef<RCCRefDropzone>(null);
	const refScrollTo = useRef<HTMLDivElement>(null);

	const [importedFiles, setImportedFiles] = useState();

	const onSubmitImport = async (dt: any) => {
		clearErrors();
		try{
			if(!dt.eftba004 || dt.eftba004.length <= 0){
				addToast({
					color: "yellow",
					description: translate("Insira ao menos um grupo de usuário"),
					title: translate("Atenção")
				});
				return;
			}

			if(!dt.eftba002 || dt.eftba002.length <= 0){
				addToast({
					color: "yellow",
					description: translate("Insira ao menos uma empresa"),
					title: translate("Atenção")
				});
				return;
			}

			if(!dt.dropZone || dt.dropZone.length <= 0){
				addToast({
					color: "yellow",
					description: translate("Insira ao menos um arquivo"),
					title: translate("Atenção")
				});
				return;
			}
			
			const dataChanged = dt;

			dataChanged.eftba004 = dt?.eftba004?.filter((efa004: any) => efa004.efa004Id &&  efa004.efa004Id !== "0")?.map((efa004: any) => Number(efa004.efa004Id));
			dataChanged.eftba002 = dt?.eftba002?.filter((efa002: any) => efa002.efa002Id &&  efa002.efa002Id !== "0")?.map((efa002: any) => Number(efa002.efa002Id));

			const formData = new FormData();
			formData.append("Delimiter", dt.delimiter);
			dt.dropZone.forEach((attach: any, index: number) => {
				formData.append(`Attachments[${index}]`, attach);
			});

			formData.append("Efa001Id", businessGroupId.current.toString());
			formData.append("Eftba004", JSON.stringify(dataChanged.eftba004));
			formData.append("Eftba002", JSON.stringify(dataChanged.eftba002));	
			formData.append("GeneratePassword", dataChanged.generatePassword ? "S" : "N");	
			
			modalRef.current?.setLoadingButton();

			const response = await RCCServicesApi.postRequest({
				method: "User/ImportUsers",
				contentType: "multipart/form-data",
				body: formData
			});

			if(response.data.length > 0)
				setImportedFiles(() => response.data);

			refScrollTo.current?.scrollIntoView({
				behavior: "smooth"
			});

			addToast({
				color: "blue",
				description: translate("Visualize os resultados na aba de dados gerais"),
				title: translate("Informação")
			});

			modalRef.current?.resetLoadingButton();

			paginationRef.current?.reload();

		}catch(err: any){
			modalRef.current?.resetLoadingButton();
			standardErrorMessages(err, translate("Erro ao importar usuários"), addToast);
		}	
	};

	const delimiterWatch = useWatch({
		control,
		name: "delimiter",
		defaultValue:";"
	});

	const generatePasswordWatch = useWatch({
		control,
		name: "generatePassword",
		defaultValue: null
	});
    

	useImperativeHandle(ref,() => ({
		actionButtonImportUsers: (efa001Id?: number) => {
			reset();
			setValue("delimiter", ";");
			setImportedFiles(undefined);
			if(efa001Id)
				businessGroupId.current = efa001Id;
			modalRef.current?.openModal();
		}
	}));

	return (
		<>
			<Modal
				ref={modalRef}
				header={{
					title: translate("Importar usuários"),
					icon: {
						name: "users"
					}
				}}
				footer= {{
					showSaveButton: true,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Importar"),
					cancelButtonText: translate("Cancelar")
				}}
				onSubmit={handleSubmit(onSubmitImport)}
				withoutForm	
			>
				<RCCGrid container item 
					styleInline={css`&.MuiGrid-root {
						@media (min-width: 960px) {
							flex-wrap: nowrap;
						}
					}`}
				>
					<RCCAlert
						title={`${translate("Documentos delimitados")}!`}
						description={<span>{`${translate("Selecione os arquivos que deseja importar para o sistema")}. ${translate("Podem ser arquivos delimitados TXT ou CSV e planilhas XLS, XLSX ou ODS")}`}.
							<a href="https://connector.effectiveerp.com.br/Manuais/Central/5" target="_blank" rel="noreferrer"><u>{translate("Acesse o manual completo")}.</u></a></span>}
					/>
					<RCCAlert
						title={`${translate("Arquivos compactados")}!`}
						description={`${translate("Para facilitar o envio, compacte os arquivos em extensões .zip ou .rar para enviar")}.`}
						styleInline="@media (min-width: 960px) {
							margin-left:8px
						};"
					/>
					<RCCAlert
						title={`${translate("Máximo 10Mb")}!`}
						description={`${translate("Cada arquivo poderá conter no máximo 10Mb de tamanho")}.`}
						styleInline="@media (min-width: 960px) {
							margin-left:8px
						};"
					/>
				</RCCGrid>
				<Tabs 
					name="tabUserMaintenance"
					tabInit={0}
					options={[{
						label: translate("Dados Gerais"),
						content: <>
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid container 
									justifyContent="center" 
									alignContent="center" 
									alignItems="center"
								>
									<RCCGrid item xs={12} md={8} lg={7}>
										<RCCGrid container item>
											<LovDelimiter
												control={control}
												name="delimiter"
												error={errors.delimiter}
											/>
											<RCCSwitch
												name="generatePassword"
												register={register}
												label={translate("Gerar senha aleatória")}
												labelPosition="end"
											/>
											<StyledFieldset>
												<legend>
													<Title size="xs" color="purple">
														{translate("Posição das colunas")}
													</Title>
												</legend>
												<RCCGrid container justifyContent="center" >
													<Title size="sm" color="defaultColor">
														{translate("Nome")}
													</Title>
													{delimiterWatch}
													<Title size="sm" color="defaultColor">
														{translate("E-mail")}
													</Title>
													{!generatePasswordWatch && (
														<>
															{delimiterWatch}
															<Title size="sm" color="defaultColor">
																{translate("Senha")}
															</Title>
														</>
													)}			
												</RCCGrid>
											</StyledFieldset>
										</RCCGrid>
										<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
											<Attachments 
												name="dropZone"
												setValue={setValue}
												ref={dropZoneRef}
												acceptedFormats=".csv,.txt,.xls,.xlsx,.ods,.rar,.zip"
											/>
										</CheckPermission>
									</RCCGrid>
									<ListImportFiles 
										importedFiles={importedFiles}
									/>
									<div ref={refScrollTo}/>
								</RCCGrid>
							</RCCGrid>
						</>
					},{
						label: translate("Grupo de usuários"),
						content: <>
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid container 
									justifyContent="center" 
									alignContent="center" 
									alignItems="center"
								>
									<RCCGrid item xs={12} md={8} lg={7}>
										<GroupsUser 
											clearErrors={clearErrors}
											control={control}
											defaultValue={[]}
											error={errors}
										/>
									</RCCGrid>
								</RCCGrid>
							</RCCGrid>
						</>
					},
					{
						label: translate("Empresas"),
						content: <>
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid container 
									justifyContent="center" 
									alignContent="center" 
									alignItems="center"
								>
									<RCCGrid item xs={12} md={8} lg={7}>
										<CompaniesUser
											clearErrors={clearErrors}
											control={control}
											defaultValue={[]}
											error={errors}
										/>
									</RCCGrid>
								</RCCGrid>
							</RCCGrid>
						</> 
					}]}
				/>
			</Modal>
		</>
	);

};


export default forwardRef(ModalImportUsers);
